import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useLocationService from '../../app/services/hooks/useLocationService';
import { Location, LocationFilters, defaultLocationFilters } from '../../app/models/Location';
import LocationDataTable from './components/tables/LocationDataTable';
import LocationEdit from './components/LocationEdit';
import LocationCreate from './components/LocationCreate';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Ubicaciones',
        url: '/locations',
        isActive: true
    }
];

const Locations = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetLocations, getLocations, deleteLocation } = useLocationService();
    const [locations, setLocations] = useState<Location[]>([]);
    const [locationIdEdit, setLocationIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [locationFilter, setLocationFilter] = useState<LocationFilters>(defaultLocationFilters);

    useEffect(() => {
        reloadTable();
    }, [locationFilter]);

    const getAllLocations = () => {
        getLocations(locationFilter, {
            onSuccess: (response: ServiceResponse) => {
                setLocations(response.data.locations);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (locationId: number) => {
        setShowingEdit(true);
        setLocationIdEdit(locationId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setLocationIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllLocations();
    };

    const destroy = (locationId: number) => {
        const _text = 'Está a punto de eliminar la ubicación #' + locationId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando ubicación...');
                        deleteLocation(locationId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La ubicación no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Ubicaciones" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva ubicación" />}
                />
                <LocationDataTable
                    locations={locations}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetLocations}
                    filter={locationFilter}
                    setFilter={setLocationFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Ubicación"
                    backdrop={true}
                    showFooter={false}
                >
                    <LocationCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Ubicación"
                    backdrop={true}
                    showFooter={false}
                >
                    <LocationEdit
                        locationId={locationIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Locations;
